import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Container,
  CircularProgress
} from '@mui/material';
import {
  School,
  Delete,
  Add
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import FileUploadComponent from '../common/FileUploadComponent';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import states from '../../utils/states.json';

const EducationSection = ({
  educationList = [],
  onEducationListChange,
  onFileUpload,
  onFileRemove,
  validationErrors = {},
  onClearError,
  isSubmitting,
  educationLevel,
  rejectedDocs
}) => {
  const theme = useTheme();

  const getRejectionError = (idx) => {
    if (!rejectedDocs) return null;
    const doc = rejectedDocs.find(d => d.documentType === 'Education Document' && d.index === idx);
    return doc ? `Rejected: ${doc.rejectionReason}` : null;
  };

  // Maps field name to the error key prefix used in validationErrors
  const fieldToErrorKey = {
    qualificationLevel: 'qualification',
    degree: 'degree',
    collegeName: 'college',
    affiliatedUniversity: 'affiliatedUniversity',
    state: 'state',
    yearOfJoining: 'joining',
    yearOfCompletion: 'completion',
    cgpaPercentage: 'cgpa',
    attachments: 'attachments',
  };

  const handleEducationChange = useCallback((index, field, value) => {
    onEducationListChange(prev =>
      (prev || []).map((item, i) => i === index ? { ...(item || {}), [field]: value } : item)
    );
    if (onClearError) {
      const errorPrefix = fieldToErrorKey[field] || field;
      onClearError(`${errorPrefix}_${index}`);
    }
  }, [onEducationListChange, onClearError]);

  const addEducation = useCallback(() => {
    onEducationListChange(prev => [
      ...(prev || []),
      {
        qualificationLevel: "",
        degree: "",
        branch: "",
        collegeName: "",
        affiliatedUniversity: "",
        yearOfJoining: "",
        yearOfCompletion: "",
        cgpaPercentage: "",
        state: "",
        attachments: []
      }
    ]);
  }, [onEducationListChange]);

  const removeEducation = useCallback((index) => {
    onEducationListChange(prev => (prev || []).filter((_, i) => i !== index));
  }, [onEducationListChange]);

  // Premium Custom Card Styling
  const customCardStyle = {
    borderRadius: '16px',
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.08),
    boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.02)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 30px -4px rgba(117, 34, 209, 0.08)',
      borderColor: alpha(theme.palette.primary.main, 0.2)
    },
    overflow: 'hidden'
  };

  const cardHeaderStyle = (title, icon, onDelete) => (
    <Box sx={{
      px: { xs: 3, md: 4 },
      py: 3,
      borderBottom: '1px solid',
      borderColor: alpha(theme.palette.primary.main, 0.06),
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: 'primary.main'
        }}>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {title}
        </Typography>
      </Box>
      {onDelete && (
        <IconButton
          onClick={onDelete}
          sx={{
            color: 'error.main',
            bgcolor: alpha(theme.palette.error.main, 0.05),
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12) }
          }}
          size="small"
        >
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );

  const inputGlobalStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: '#F8FAFC',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#E2E8F0',
        transition: 'all 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        bgcolor: alpha(theme.palette.action.disabledBackground, 0.03),
      },
      '&.Mui-disabled fieldset': {
        borderColor: '#F1F5F9',
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover fieldset': {
        borderColor: theme.palette.error.dark,
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-select': {
      borderRadius: '12px',
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ position: 'relative' }}>
        {isSubmitting && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000,
              borderRadius: '16px',
              backdropFilter: 'blur(4px)',
            }}
          >
            <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
          </Box>
        )}
        <Box sx={inputGlobalStyles}>
          {/* Top Welcome Banner Card */}
          <Card elevation={0} sx={{
            mb: 4,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #4D1293 100%)`,
            color: '#FFFFFF',
            p: { xs: 3, md: 4 },
            position: 'relative',
            boxShadow: '0 10px 25px -5px rgba(117, 34, 209, 0.3)'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.5px' }}>
                  Education Details
                </Typography>
                <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.8), fontWeight: 400 }}>
                  Tell us about your educational background.
                </Typography>
              </Box>
              <School sx={{ fontSize: 80, color: alpha('#FFFFFF', 0.15), display: { xs: 'none', md: 'block' } }} />
            </Box>
          </Card>

          {educationList.map((edu, idx) => (
            <Card key={idx} elevation={0} sx={{ ...customCardStyle, mb: 3 }}>
              {cardHeaderStyle(`Education #${idx + 1}`, <School />, educationList.length > 1 ? () => removeEducation(idx) : null)}
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Qualification Level*</InputLabel>
                      <Select
                        value={edu.qualificationLevel}
                        onChange={e => handleEducationChange(idx, 'qualificationLevel', e.target.value)}
                        label="Qualification Level"
                        required
                        error={!!validationErrors[`qualification_${idx}`]}
                      >
                        <MenuItem value=""></MenuItem>
                        <MenuItem value="10th">10th</MenuItem>
                        <MenuItem value="12th">12th/Diploma</MenuItem>
                        {educationLevel !== 'level1' && (
                          <MenuItem value="Graduate">Graduate</MenuItem>
                        )}
                        {educationLevel !== 'level1' && (
                          <MenuItem value="Post Graduate">Post Graduate</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    {validationErrors[`qualification_${idx}`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2, display: 'block' }}>
                        {validationErrors[`qualification_${idx}`]}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      label="Degree/Certificate Name"
                      variant="outlined"
                      value={edu.degree}
                      onChange={e => handleEducationChange(idx, 'degree', e.target.value)}
                      fullWidth
                      required
                      error={!!validationErrors[`degree_${idx}`]}
                      helperText={validationErrors[`degree_${idx}`]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      label="Branch/Specialization"
                      variant="outlined"
                      value={edu.branch}
                      onChange={e => handleEducationChange(idx, 'branch', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <TextField
                      label="College/School Name"
                      variant="outlined"
                      value={edu.collegeName}
                      onChange={e => handleEducationChange(idx, 'collegeName', e.target.value)}
                      fullWidth
                      required
                      error={!!validationErrors[`college_${idx}`]}
                      helperText={validationErrors[`college_${idx}`]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      label="University/Board"
                      variant="outlined"
                      value={edu.affiliatedUniversity}
                      onChange={e => handleEducationChange(idx, 'affiliatedUniversity', e.target.value)}
                      fullWidth
                      required
                      error={!!validationErrors[`affiliatedUniversity_${idx}`]}
                      helperText={validationErrors[`affiliatedUniversity_${idx}`]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>State*</InputLabel>
                      <Select
                        value={edu.state}
                        onChange={e => handleEducationChange(idx, 'state', e.target.value)}
                        label="State"
                        required
                        error={!!validationErrors[`state_${idx}`]}
                      >
                        <MenuItem value="">Select State</MenuItem>
                        {states.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {validationErrors[`state_${idx}`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2, display: 'block' }}>
                        {validationErrors[`state_${idx}`]}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <DatePicker
                      label="Year of Joining*"
                      value={edu.yearOfJoining ? new Date(edu.yearOfJoining, 0, 1) : null}
                      onChange={(date) => handleEducationChange(idx, 'yearOfJoining', date ? date.getFullYear() : null)}
                      maxDate={edu.yearOfCompletion ? new Date(edu.yearOfCompletion, 0, 1) : new Date()}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          error={!!validationErrors[`joining_${idx}`]}
                          helperText={validationErrors[`joining_${idx}`] || (!edu.yearOfJoining ? "Year of joining is required" : "")}
                        />
                      )}
                      views={['year']}
                      openTo="year"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <DatePicker
                      label="Year of Completion*"
                      value={edu.yearOfCompletion ? new Date(edu.yearOfCompletion, 0, 1) : null}
                      onChange={(date) => handleEducationChange(idx, 'yearOfCompletion', date ? date.getFullYear() : null)}
                      minDate={edu.yearOfJoining ? new Date(edu.yearOfJoining, 0, 1) : null}
                      maxDate={new Date()}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          error={!!validationErrors[`completion_${idx}`]}
                          helperText={validationErrors[`completion_${idx}`] || (!edu.yearOfCompletion ? "Year of completion is required" : "")}
                        />
                      )}
                      views={['year']}
                      openTo="year"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      label="CGPA/Percentage"
                      variant="outlined"
                      value={edu.cgpaPercentage}
                      onChange={e => handleEducationChange(idx, 'cgpaPercentage', e.target.value)}
                      fullWidth
                      required
                      type="number"
                      inputProps={{
                        step: "0.01",
                        min: "0"
                      }}
                      error={!!validationErrors[`cgpa_${idx}`]}
                      helperText={validationErrors[`cgpa_${idx}`] || "Enter CGPA (e.g., 8.5) or Percentage (e.g., 75)"}
                    />
                  </Grid>
                </Grid>

                {/* File Upload Section */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600 }}>
                    Attach Documents (Certificates, Mark Sheets, etc.)
                  </Typography>
                  <FileUploadComponent
                    label="Upload Documents"
                    files={edu.attachments || []}
                    onFileUpload={(files) => onFileUpload(files, 'education', idx)}
                    onFileRemove={(index) => onFileRemove('education', idx, index)}
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    required={!!(edu.qualificationLevel && edu.degree)}
                    error={!!validationErrors[`attachments_${idx}`] || !!getRejectionError(idx)}
                    helperText={getRejectionError(idx) || validationErrors[`attachments_${idx}`]}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}

          <Button
            variant="outlined"
            onClick={addEducation}
            startIcon={<Add />}
            sx={{
              mt: 2,
              borderRadius: '12px',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.25,
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px',
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              mx: { xs: 2, md: 0 }
            }}
          >
            Add Education
          </Button>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default EducationSection;
